import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, User, Menu, X, Globe, LogOut } from "lucide-react";
import { languages } from "../mock";
import { useAuth } from "../context/AuthContext";
import CurrencySwitcher from "./CurrencySwitcher";
import NotificationBell from "./NotificationBell";
import Logo from "./Logo";

const eventLinks = [
  { label: "Weddings", href: "/weddings" },
  { label: "Parties", href: "/party" },
  { label: "Business", href: "/business" },
  { label: "Memorials", href: "/memorials" },
  { label: "Engagements", href: "/engagements" },
  { label: "Kids Parties", href: "/kids-parties" }
];

const pricingLinks = [
  { label: "Wedding Pricing", href: "/weddings-pricing" },
  { label: "Engagement Pricing", href: "/engagements-pricing" },
  { label: "Party Pricing", href: "/party-pricing" },
  { label: "Business Pricing", href: "/business-pricing" },
  { label: "Memorial Pricing", href: "/memorials-pricing" },
  { label: "Kids Party Pricing", href: "/kids-party-pricing" }
];

const aboutLinks = [
  { label: "Our Story", href: "/our-story" },
  { label: "How It Works", href: "/how-it-works" },
  { label: "Photo Sharing App", href: "/photo-sharing-app" },
  { label: "Reviews", href: "/reviews" }
];

const Dropdown = ({ label, items }) => (
  <div className="relative group">
    <button className="flex items-center gap-1 text-navy-700 hover:text-coral-300 transition-colors font-medium text-[15px] py-2">
      {label}
      <ChevronDown className="w-4 h-4" />
    </button>
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
      <div className="bg-white rounded-xl shadow-xl border border-cream-200 py-2 min-w-[220px]">
        {items.map((it) => (
          <Link key={it.href} to={it.href} className="block px-5 py-2.5 text-[14px] text-navy-700 hover:bg-cream-100 hover:text-coral-300 transition-colors">
            {it.label}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("EN");
  const [langOpen, setLangOpen] = useState(false);
  const { user, logout } = useAuth() || {};

  return (
    <header className="sticky top-0 z-50 bg-cream-100/95 backdrop-blur-md border-b border-cream-200">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="header-logo-link">
          <Logo size="md" tint="coral" asStatic />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <Dropdown label="Events" items={eventLinks} />
          <Link to="/how-it-works" className="text-navy-700 hover:text-coral-300 transition-colors font-medium text-[15px]">How It Works</Link>
          <Dropdown label="Pricing" items={pricingLinks} />
          <Link to="/partners" className="text-navy-700 hover:text-coral-300 transition-colors font-medium text-[15px]">Partners</Link>
          <Dropdown label="About" items={aboutLinks} />
          <Link to="/help-center" className="text-navy-700 hover:text-coral-300 transition-colors font-medium text-[15px]">Help Center</Link>
          <Link to="/contact" data-testid="header-contact-link" className="text-navy-700 hover:text-coral-300 transition-colors font-medium text-[15px]">Contact</Link>
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link to={user ? "/dashboard" : "/login"} className="w-9 h-9 rounded-full bg-coral-300/15 text-coral-300 flex items-center justify-center hover:bg-coral-300/25 transition-colors" aria-label="Account">
            <User className="w-4 h-4" />
          </Link>
          {user && <NotificationBell />}
          <CurrencySwitcher />
          <div className="relative">
            <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-2 border border-navy-700/20 rounded-md px-3 py-1.5 text-sm text-navy-700 hover:border-coral-300 transition-colors">
              <Globe className="w-3.5 h-3.5" />
              {lang}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-cream-200 py-2 min-w-[180px] z-50">
                {languages.map((l) => (
                  <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-navy-700 hover:bg-cream-100 hover:text-coral-300 transition-colors">
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link to={user ? "/dashboard" : "/get-started"} className="border-2 border-coral-300 text-coral-300 px-5 py-2 rounded-md font-medium text-sm hover:bg-coral-300 hover:text-white transition-colors">
            {user ? "Dashboard" : "Get Started Free"}
          </Link>
          {user && (
            <button onClick={logout} className="text-navy-700/60 hover:text-coral-300" title="Sign out"><LogOut className="w-4 h-4" /></button>
          )}
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden text-navy-700" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-cream-200 px-5 py-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="py-2 font-semibold text-navy-700">Events</div>
          {eventLinks.map(l => <Link key={l.href} to={l.href} className="block py-2 pl-3 text-sm text-navy-700/80" onClick={() => setOpen(false)}>{l.label}</Link>)}
          <Link to="/how-it-works" onClick={() => setOpen(false)} className="block py-3 font-semibold text-navy-700 border-t border-cream-200 mt-2">How It Works</Link>
          <Link to="/partners" onClick={() => setOpen(false)} className="block py-3 font-semibold text-navy-700 border-t border-cream-200 mt-2">Partners</Link>
          <div className="py-2 font-semibold text-navy-700 border-t border-cream-200 mt-2">Pricing</div>
          {pricingLinks.map(l => <Link key={l.href} to={l.href} className="block py-2 pl-3 text-sm text-navy-700/80" onClick={() => setOpen(false)}>{l.label}</Link>)}
          <Link to="/help-center" onClick={() => setOpen(false)} className="block py-3 font-semibold text-navy-700 border-t border-cream-200 mt-2">Help Center</Link>
          <Link to="/get-started" onClick={() => setOpen(false)} className="mt-4 block text-center border-2 border-coral-300 text-coral-300 px-5 py-2.5 rounded-md font-medium">Get Started Free</Link>
        </div>
      )}
    </header>
  );
};

export default Header;
